import { useEffect, useMemo, useState } from "react";
import { useForm, useFormContext, useWatch } from "react-hook-form";
import { useIndex } from "@/src/hooks/useIndex";
import { cloneDeep, has, isNil, toNumber } from "lodash";
import {
  onCurrencySelectChangeProps, ProposalTableColumnProps,
  UseProposalTableActionsParams
} from "./type";
import {
  getColContextValueString,
  getFieldName,
  runCallbackForFieldType,
} from "./utils";
import {
    COUNTRY_TRAVEL_ZONE_CONST,
    DAYS_ABROAD_CONST,
    FOR_TOURISTS_CONST,
    INSURED_COUNT_VAR_LIST,
    INSURED_TOURISTS_CONST,
    isVisaCentreCategory,
    PLACE_OF_STAY_ABROAD_CONST,
    PROPOSAL_TABLE_KEY,
    proposalData,
    ProposalDataType,
    TERRORISM_CONST,
    TRAVEL_PURPOSE_CONST,
    visaCentreProposalData,
    WORK_CONST
} from "./const";
import { useFieldDetailInfoModal } from "@/src/stores/useFieldDetailInfoModal";
import { FieldDetailInfoModalInitialStateProps } from "@/src/stores/lib/type";
import { ProposalTableColumnFieldTypes } from "@/src/shared/schemas";
import { runAction } from "@/src/api/server-actions/action";
import { useSearchParams } from "next/navigation";
import { PROCESS_INSTANCE_QUERY } from "@/src/shared/const";
import { useProcessInstanceStore } from "@/src/stores/useProcessInstanceStore";
import { useContextStore } from "@/src/stores/useContextStore";
import {
  formatContextVariablesListToFormValues,
  formatFormDataToVarList,
} from "@/src/shared/utils/form";
import { parseContextVariableValue } from "@/src/shared/utils/dataTypeHelpers";
import { ApiCallsTypes } from "@/src/components/pages/StepPage/components/apiCalls/contants";
import { useFormDataMiddleware } from "@/src/components/Action/useFormDataMiddleware";
import { getContextVariablesToApiCall } from "@/src/components/Action/utils";
import { useKeysToUpdate } from "@/src/components/Action/useKeysToUpdate";
import { useStepsNavigation } from "@/src/components/pages/StepPage/components/apiCalls/useStepsNavigation";
import {useAnalytics} from "@/src/components/pages/StepPage/components/useAnalytics";
import { proposalSelectionPendingStore } from "./proposalSelectionPendingStore";

/**
 *
 * Function to filter proposalData. Leaves only those records where columnId
 * is included in the title list from the columns array.
 *
 * @param proposalData - object with data arrays
 * @param columns - array of columns with titles to filter
 * @returns filtered proposalData
 */
export function filterProposalData(
  proposalData: ProposalDataType,
  columns: ProposalTableColumnProps[]
): ProposalDataType {
  // Create a set of valid titles to speed up the search
  const validTitles = new Set(columns.map((col) => col.title));

  // Create an empty object to store the filtered data
  const filteredData = {} as ProposalDataType;

  for (const key in proposalData) {
    if (Object.prototype.hasOwnProperty.call(proposalData, key)) {
      // Filter the data array by the valid titles
      filteredData[key as keyof ProposalDataType] = proposalData[key as keyof ProposalDataType].filter(
        (item) => validTitles.has(item.columnId)
      );
    }
  }

  return filteredData;
}

export const useProposalTable = ({
  columnName,
  columns,
  contextFieldNames,
}: UseProposalTableActionsParams) => {
  const { trackAnalyticEvent } = useAnalytics();

  const { index: selectedColumnIndex, handleSetIndex: setSelectedColumnIndex } =
    useIndex({ initialIndex: undefined });

  const {
    index: editingColumnIndex,
    handleSetIndex: setEditingColumnIndex,
    resetIndex: resetEditingColumnIndex,
  } = useIndex({ initialIndex: undefined });

  const { index: openedHintAreaIndex, handleToggleIndex: toggleHintArea } =
    useIndex({});
  const searchParams = useSearchParams();
  const processInstanceId = searchParams.get(PROCESS_INSTANCE_QUERY);
  const processInstance = useProcessInstanceStore(
    (state) => state.processInstance,
  );
  const getVariable = useContextStore((state) => state.getVariable);
  const replaceVariablesObject = useContextStore((state) => state.replaceVariablesObject);
  const openHintDetailsModal = useFieldDetailInfoModal((state) => state.open);

  const { formDataMiddleware } = useFormDataMiddleware();
  const { getKeysToUpdate } = useKeysToUpdate();
  const { goNext } = useStepsNavigation();

  const [beforeEditData, setBeforeEditData] = useState(null);
  const [isTariffCalcPending, setIsTariffCalcPending] = useState(false);

  const {
    getValues: getMainFormContext,
    setValue: setMainFormValue,
    control: mainFormControl,
    reset: resetMainForm,
  } = useFormContext();
  const form = useForm();
  const { setValue, getValues } = form;
  const selectedTouristColumnId = useWatch({
    control: mainFormControl,
    name: columnName,
  });

  const proposalDataMemo = useMemo(() => {
    const isVisaCentre = columns?.some((column) => isVisaCentreCategory(column?.title));
    return filterProposalData(isVisaCentre ? visaCentreProposalData : proposalData, columns)
  }, [columns])

  const tariffCalculatorActionName = useMemo(() => {
    return columns?.some((column) => isVisaCentreCategory(column?.title))
      ? ApiCallsTypes.VISA_CENTER_TARIFF_CALCULATOR
      : ApiCallsTypes.TRAVEL_TARIFF_CALCULATOR;
  }, [columns]);

  useEffect(() => {
    if (!selectedTouristColumnId || !columns || columns.length === 0) return;
  
    const selectedTouristColumnIndex = columns.findIndex(
      (column) => column?.title === selectedTouristColumnId
    );
  
    if (selectedTouristColumnIndex === -1) return;
  
    setSelectedColumnIndex(selectedTouristColumnIndex);
  
    columns.forEach((column) => {
      if (!column?.title) return;
  
      contextFieldNames.forEach((fieldName) => {
        if (!fieldName) return;
  
        const name = `${PROPOSAL_TABLE_KEY}.${column.title}.${fieldName}`;
        const mainContextName = getColContextValueString(column.title);
  
        if (!mainContextName) return;
  
        const defaultValue = getMainFormContext(mainContextName)?.[fieldName] ?? null;
  
        if (isNil(defaultValue)) return;
  
        setValue(name, defaultValue);
      });
    });
  }, [selectedTouristColumnId, columns, contextFieldNames, setValue, getMainFormContext]);

  const prepareContextVariables = (columnId: string) => {
    const tableValues = getValues(PROPOSAL_TABLE_KEY);
    const insuredTourists = getMainFormContext(INSURED_TOURISTS_CONST) as Record<
      string,
      number
    >;
    if (!columnId || !has(tableValues, columnId)) return null;

    const valuesToUpdate = tableValues[columnId];
    const staticValueKey = getColContextValueString(columnId);
    const columnStaticValues = getMainFormContext(staticValueKey);

    const daysAbroad = getMainFormContext(DAYS_ABROAD_CONST);
  
    if (isNil(daysAbroad)) return;
  
    const apiCallActionParams = {
      countryTravelZone: getMainFormContext(COUNTRY_TRAVEL_ZONE_CONST),
      work: getMainFormContext(WORK_CONST),
      daysAbroad: toNumber(daysAbroad),
      terrorism: getMainFormContext(TERRORISM_CONST),
      travelPurpose: getMainFormContext(TRAVEL_PURPOSE_CONST),
      placeOfStayAbroad: getMainFormContext(PLACE_OF_STAY_ABROAD_CONST),
    };

    const ageVariables = INSURED_COUNT_VAR_LIST.reduce<Record<string, number>>(
      (acc, key) => {
        acc[key] = insuredTourists[key];
        return acc;
      },
      {},
    );

    const civilCurrency =
        daysAbroad <= 30
            ? valuesToUpdate.helpAssistance_Currency
            : valuesToUpdate.civilCurrency;

    return {
      ...columnStaticValues,
      ...valuesToUpdate,
      ...apiCallActionParams,
      ...ageVariables,
      [staticValueKey]: {
        ...columnStaticValues,
        ...valuesToUpdate,
        FinancialRisk_Currency: civilCurrency?.value, // financialRisk_Currency = civilCurrency = helpAssistance_Currency
        financialRisk_Currency: civilCurrency?.value // financialRisk_Currency = civilCurrency = helpAssistance_Currency
      },
      [FOR_TOURISTS_CONST]: getMainFormContext(FOR_TOURISTS_CONST),
      insuredCount: getMainFormContext("insuredCount"),
      insuredTourists: insuredTourists,
      pickedTravelCalculationCategory: parseContextVariableValue(columnId),
      calculationCategory: parseContextVariableValue(columnId),
      FinancialRisk_Currency: civilCurrency?.value, // financialRisk_Currency = civilCurrency = helpAssistance_Currency
      financialRisk_Currency: civilCurrency?.value // financialRisk_Currency = civilCurrency = helpAssistance_Currency
    };
  };

  const performTariffCalculation = async (
    contextVariables: Record<string, any>,
  ) => {
    if (!contextVariables) return;
    const formatedVariables = formatFormDataToVarList(contextVariables);

    const requestBody = {
      id: processInstanceId,
      processId: processInstance.processId,
      activeStepId: processInstance.activeStepId,
      contextInstance: {
        id: processInstance.contextInstance.id,
        variables: formatedVariables,
      },
    } as any;

    const tariffResponse = await runAction({
      actionName: tariffCalculatorActionName,
      body: requestBody,
    });

    const pickTariffResponse = await runAction({
      actionName: ApiCallsTypes.PICK_TRAVEL_TARIFF_CATEGORY_ACTION,
      body: {
        ...requestBody,
        contextInstance: {
          id: requestBody.contextInstance.id,
          variables: tariffResponse.contextInstance.variables,
        },
      },
    });

    const mergedValues = {
      ...getMainFormContext(),
      ...formatContextVariablesListToFormValues(
        tariffResponse.contextInstance.variables,
      ),
    };
    replaceVariablesObject(pickTariffResponse.contextInstance.variables);
    resetMainForm(mergedValues, { keepDirty: true });
    return { pickTariffResponse, tariffResponse };
  };

  const updateMainFormValues = async (columnId: string) => {
    const contextVariablesToUpdate = prepareContextVariables(columnId);
    if (!contextVariablesToUpdate) return;

    setIsTariffCalcPending(true);
    proposalSelectionPendingStore.getState().setIsSelectionPending(true);

    try {
      const { tariffResponse } = (await performTariffCalculation(
        contextVariablesToUpdate,
      )) || { tariffResponse: null };

      if (tariffResponse) {
        Object.keys(contextVariablesToUpdate).forEach((key) => {
          const columnFieldKey = `${getColContextValueString(columnId)}.${key}`;
          setMainFormValue(columnFieldKey, contextVariablesToUpdate[key], {
            shouldDirty: true,
          });
        });
        setMainFormValue("calculationCategory", columnId);
      }
    } catch (e) {
      console.error("Error updating main form values:", e);
    } finally {
      setIsTariffCalcPending(false);
      proposalSelectionPendingStore.getState().setIsSelectionPending(false);
    }
  };

  const onCancel = (columnIndex?: number, columnId?: string) => {
    columnId && trackAnalyticEvent(`cancel_${columnId.toLowerCase()}` as "cancel_standard");
    resetEditingColumnIndex();
    if (beforeEditData) {
      setValue(PROPOSAL_TABLE_KEY, beforeEditData);
    }
    setBeforeEditData(null);
  };

  const onRecalculate = async (columnIndex: number, columnId: string) => {
    trackAnalyticEvent(`recalc_${columnId.toLowerCase()}` as "recalc_standard");
    await updateMainFormValues(columnId);
    resetEditingColumnIndex();
    setBeforeEditData(null);
  };

  const onSelectColumnCard = async (columnIndex: number, columnId: string) => {
    trackAnalyticEvent(`select_${columnId.toLowerCase()}` as "select_standard");
    if (
      !isNil(editingColumnIndex) &&
      editingColumnIndex !== columnIndex
    ) {
      setValue(PROPOSAL_TABLE_KEY, beforeEditData);
      setBeforeEditData(null);
      resetEditingColumnIndex(); 
    }
    setSelectedColumnIndex(columnIndex);

    const pickedTravelCalculationCategoryVar = getVariable(
      "pickedTravelCalculationCategory",
    );

    if (pickedTravelCalculationCategoryVar) {
      const pickedTravelCalculationCategory = parseContextVariableValue(columnId) as string;
      const staticValueKey = getColContextValueString(pickedTravelCalculationCategory);
      const columnStaticValues = getMainFormContext(staticValueKey);
      const recordData = formDataMiddleware(null, {
        ...getMainFormContext(),
        ...columnStaticValues,
        pickedTravelCalculationCategory: parseContextVariableValue(columnId),
        calculationCategory: parseContextVariableValue(columnId)
      });

      const requestBody = {
        id: processInstanceId,
        processId: processInstance.processId,
        activeStepId: processInstance.activeStepId,
        contextInstance: {
          id: processInstance.contextInstance.id,
          variables: getContextVariablesToApiCall({
            formData: recordData,
            keyToUpdate: getKeysToUpdate()
          }),
        },
      } as any;

      proposalSelectionPendingStore.getState().setIsSelectionPending(true);
      try {
        const res = await runAction({
          actionName: ApiCallsTypes.PICK_TRAVEL_TARIFF_CATEGORY_ACTION,
          body: requestBody,
        });
        replaceVariablesObject(res.contextInstance.variables);
      } finally {
        proposalSelectionPendingStore.getState().setIsSelectionPending(false);
      }
    }
  };

  const onChangeCover = async (columnIndex: number, columnId: string) => {
    trackAnalyticEvent(`change_coverage_${columnId.toLowerCase()}` as "change_coverage_standard");
    if (
      !isNil(editingColumnIndex) &&
      editingColumnIndex !== columnIndex
    ) {
      setValue(PROPOSAL_TABLE_KEY, beforeEditData);
      setBeforeEditData(null);
      resetEditingColumnIndex();
    }
  
    setEditingColumnIndex(columnIndex);
  
    const clonedData = cloneDeep(getValues(PROPOSAL_TABLE_KEY));
    setBeforeEditData(clonedData);
  
    if (columnIndex !== selectedColumnIndex) {
      setSelectedColumnIndex(columnIndex);
    }
  };

  const onHintAreaDetailsButtonPressed = ({
    title,
    description,
    actions,
  }: FieldDetailInfoModalInitialStateProps) => {
    openHintDetailsModal({ title, description, actions });
  };

  const onCurrencySelectChange = ({ value }: onCurrencySelectChangeProps) => {
    if (value?.type && activeColumnId) {
      const columnData = getValues(`${PROPOSAL_TABLE_KEY}.${activeColumnId}`);
      runCallbackForFieldType(
        columnData,
        ProposalTableColumnFieldTypes.SelectCurrency,
        (key) => {
          setValue(
            getFieldName({ columnId: activeColumnId, fieldName: key }),
            value,
          );
        },
      );
    }
  };

  const onPdfFileClick = () => {
    trackAnalyticEvent("offer_compare")
  };

  const activeColumnId = !isNil(selectedColumnIndex)
    ? columns[selectedColumnIndex]?.title
    : null;

  return {
    activeColumnId,
    selectedColumnIndex,
    editingColumnIndex,
    openedHintAreaIndex,
    toggleHintArea,
    onHintAreaDetailsButtonPressed,
    onSelectColumnCard,
    onRecalculate,
    onCancel,
    onChangeCover,
    onCurrencySelectChange,
    onPdfFileClick,
    form,
    getMainFormContext,
    isTariffCalcPending,
    proposalData: proposalDataMemo
  };
};
